import React, { useEffect, useMemo, useState } from 'react';
import NameSelect from '../../components/NameSelect';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ORG_DB_NAMES, ORG_LABELS } from '../../config';
import { useFetch } from '../../hooks';
import { Alert, Button, Field, Input, Select, Textarea } from '../../ui/basics';
import { Drawer, useToast } from '../../ui/overlay';
import { dayFromNow, rules, useFormValidation } from '../../validation';
import { DocList } from './DocEditor';

const unique = (list) => [...new Set(list.filter(Boolean))].sort((a, b) => a.localeCompare(b));

/** CC addresses typed as text: separated by commas, semicolons or spaces. */
const parseCc = (text) => {
  const items = String(text || '').split(/[,;\s]+/).filter(Boolean);
  return { list: items, bad: items.filter((a) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a)) };
};

const SCHEMA = {
  candidate_name: [rules.required('Candidate name'), rules.maxLength(100, 'Candidate name')],
  candidate_email: [rules.required('Candidate email'), rules.email('Candidate email'), rules.maxLength(100, 'Candidate email')],
  candidate_phonenumber: rules.phone('Phone number'),
  candidate_location: rules.maxLength(100, 'Candidate location'),
  client_location: rules.maxLength(100, 'Client location'),
  cc_emails: rules.check((text) => parseCc(text).bad.length === 0 && parseCc(text).list.length <= 10, 'Use up to 10 valid email addresses, separated by commas.'),
  offer_date: rules.date('Offer date', { max: dayFromNow(3 * 366) }),
  expected_start_date: [rules.date('Expected start date', { max: dayFromNow(3 * 366) }), rules.notBefore('offer_date', 'Expected start date', 'Offer date')],
  client: rules.required('Client'),
  facility: rules.maxLength(250, 'Facility'),
  job_title: rules.maxLength(250, 'Job title'),
  required_documents: rules.check((list) => list.length > 0, 'Add at least one required document.'),
};

export default function CandidateForm({ org, onClose, onSaved }) {
  const { user, orgs } = useAuth();
  const toast = useToast();
  const writable = orgs.filter((o) => o !== 'combined');
  const [orgKey, setOrgKey] = useState(org === 'combined' ? writable[0] : org);
  const [refs, setRefs] = useState([]);
  const [form, setForm] = useState({ candidate_name: '', candidate_email: '', candidate_phonenumber: '', candidate_location: '',
    client_location: '', offer_date: '', expected_start_date: '', remarks: '', recruiter: '', onboarding_executive: '', cc_emails: '', client: '', facility: '', job_title: '' });
  const [match, setMatch] = useState(null);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    api.post('Get_ClientandFacility_details/', { organization: orgKey }).then((r) => setRefs(r.data || [])).catch((e) => setError(e.message));
  }, [orgKey]);
  // Each organization has its own client references, so the picks made for the old one must not carry over.
  const changeOrg = (key) => { setOrgKey(key); setRefs([]); setForm((prev) => ({ ...prev, client: '', facility: '', job_title: '', recruiter: '', onboarding_executive: '' })); };

  const clients = useMemo(() => unique(refs.map((r) => r.client_name)), [refs]);
  const facilities = useMemo(() => unique(refs.filter((r) => r.client_name === form.client).map((r) => r.facility)), [refs, form.client]);
  const titles = useMemo(() => unique(refs.filter((r) => r.client_name === form.client && r.facility === form.facility).map((r) => r.job_title)), [refs, form.client, form.facility]);

  const pick = (key, value) => setForm((prev) => ({ ...prev, client: key === 'client' ? value : prev.client,
    facility: key === 'client' ? '' : key === 'facility' ? value : prev.facility, job_title: key === 'job_title' ? value : '' }));

  useEffect(() => {
    setDocs([]);
    setMatch(null);
    if (!form.client) return;
    let current = true;
    api.post('Get_ClientandFacility_details/', { organization: orgKey, client: form.client, facility: form.facility, job_title: form.job_title })
      .then((r) => {
        if (!current) return;
        const found = r.data && r.data[0];
        setDocs((found && found.required_documents) || []);
        setMatch(found ? found.matched_level : 'none');
      })
      .catch((e) => current && setError(e.message));
    return () => { current = false; };
  }, [orgKey, form.client, form.facility, form.job_title]);

  const v = useFormValidation({ ...form, required_documents: docs }, SCHEMA);
  const lookups = useFetch('lookups/', { organization: orgKey });
  const execs = (lookups.data ? lookups.data.executives || [] : []).map((e) => ({ value: e.email, label: e.name + (e.name === e.email ? '' : ' (' + e.email + ')') }));
  const bind = (key) => ({ value: form[key], onChange: set(key), ...v.props(key) });
  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    try {
      const res = await api.post('Add_Onboard_details/', { ...form, cc_emails: parseCc(form.cc_emails).list, user: user.email, required_documents: docs,
        organization: ORG_DB_NAMES[orgKey], createdat: new Date().toISOString() });
      const mail = res.email || {};
      toast(mail.status === 'sent' ? 'Candidate offer added and recruiter emailed' : mail.status === 'failed' ? 'Offer added, but the email was not sent. You can retry it from the offer.'
        : mail.status === 'skipped' ? 'Offer added. ' + mail.message : 'Candidate offer added', mail.status === 'sent' || !mail.status ? 'success' : mail.status === 'failed' ? 'error' : 'info');
      onSaved();
    } catch (e) { setError(v.fail(e) ? 'The server found a problem with the highlighted fields.' : e.message); setBusy(false); }
  };

  return (
    <Drawer title="Add candidate offer" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>Add candidate offer</Button></>}>
      <div className="form-grid">
        {(v.summary || error) && <div className="full"><Alert>{error || v.summary}</Alert></div>}
        {org === 'combined' && (
          <Field label="Organization *" className="full" hint="Choose which organization this candidate belongs to.">
            <Select value={orgKey} onChange={(e) => changeOrg(e.target.value)} options={writable.map((o) => ({ value: o, label: ORG_LABELS[o] }))} />
          </Field>
        )}
        <Field label="Candidate name *" error={v.shown('candidate_name')}><Input maxLength={120} {...bind('candidate_name')} /></Field>
        <Field label="Candidate email *" error={v.shown('candidate_email')}><Input type="email" autoComplete="off" {...bind('candidate_email')} /></Field>
        <Field label="Phone number" error={v.shown('candidate_phonenumber')} hint="For example +1 555 123 4567"><Input type="tel" {...bind('candidate_phonenumber')} /></Field>
        <Field label="Candidate location" error={v.shown('candidate_location')}><Input {...bind('candidate_location')} /></Field>
        <Field label="Client *" error={v.shown('client')}><Select placeholder="Select client" options={clients} {...v.props('client')} value={form.client} onChange={(e) => pick('client', e.target.value)} /></Field>
        <Field label="Facility" error={v.shown('facility')} hint="Optional. Leave empty to use the client's default documents."><Select placeholder="Any facility" options={facilities} {...v.props('facility')} value={form.facility} disabled={!form.client} onChange={(e) => pick('facility', e.target.value)} /></Field>
        <Field label="Job title" error={v.shown('job_title')} hint="Optional. Leave empty to use the facility's default documents."><Select placeholder="Any job title" options={titles} {...v.props('job_title')} value={form.job_title} disabled={!form.facility} onChange={(e) => pick('job_title', e.target.value)} /></Field>
        <Field label="Client location" error={v.shown('client_location')}><Input {...bind('client_location')} /></Field>
        <Field label="Offer date" error={v.shown('offer_date')} hint="When the offer was made."><Input type="date" {...bind('offer_date')} /></Field>
        <Field label="Expected start date" error={v.shown('expected_start_date')}><Input type="date" {...bind('expected_start_date')} /></Field>
        <Field label="Recruiter" error={v.shown('recruiter')} hint="Optional. The recruiter handling this candidate.">
          <NameSelect kind="recruiter" lookups={lookups} placeholder="No recruiter" {...bind('recruiter')} /></Field>
        <Field label="Onboarding executive" error={v.shown('onboarding_executive')} hint="Optional. Gets the document due-date reminders. Leave empty to use yourself.">
          <Select placeholder="Me (the person adding the offer)" options={execs} {...bind('onboarding_executive')} /></Field>
        <Field label="CC" error={v.shown('cc_emails')} hint="Optional. Others to keep informed: email addresses separated by commas.">
          <Input autoComplete="off" placeholder="name@company.com, other@company.com" {...bind('cc_emails')} /></Field>
        <Field label="Remarks" className="full"><Textarea value={form.remarks} onChange={set('remarks')} /></Field>
        <Field label="Required documents *" className="full" error={v.shown('required_documents')} hint={match === 'none' ? 'No reference matches this choice. Add one under Client references, or pick another facility.' : match ? 'Using the ' + match + ' reference. Remove or add documents for this candidate.' : 'Pick a client to load its documents. Remove or add documents for this candidate.'}>
          <DocList docs={docs} onChange={setDocs} />
        </Field>
      </div>
    </Drawer>
  );
}
